<template>
  <div class="cnc-icon">
    <SvgIcon
      v-if="isSvgIcon"
      :name="iconName"
      :width="width"
      :color="color"
      :color2="color2"
      :color3="color3"
      :isTwoTone="isTwoTone"
      :isSolid="isSolid"
      :isGradient="isGradient"
      :bgColor="bgColor"
      :padding="padding"
      :borderRadius="borderRadius"
      :hasState="hasState"
      :colorState="colorState"
      :state="state"
      :isActive="isActive"
      :isFixedColor="isFixedColor"
      :isOutline="isOutline"
    />
    <Icon
      v-else
      :name="name"
      :width="width"
      :style="{ color, 'font-size': `${width}px !important` }"
      :className="className"
      :qa="qa"
    />
  </div>
</template>

<script>
export default {
  name: 'CncIcon',
  props: {
    name: { type: String, required: true },
    width: { type: Number, default: 24 },
    color: { type: String },
    color2: { type: String },
    color3: { type: String },
    isTwoTone: { type: Boolean },
    isSolid: { type: Boolean },
    isGradient: { type: Boolean },
    bgColor: { type: String },
    padding: { type: String },
    borderRadius: { type: String },
    hasState: { type: Boolean },
    colorState: { type: Object },
    state: { type: String },
    isActive: { type: Boolean },
    className: { type: String },
    qa: { type: String },
    isFixedColor: { type: Boolean },
    isOutline: { type: Boolean }
  },
  computed: {
    isSvgIcon () {
      if (!this.name) return false
      const isComponentOld = this.name.startsWith('Icon')
      const isComponentNew = this.name.startsWith('Ico')
      const isNameNewFormat = this.name.startsWith('ico')
      return isComponentOld || isComponentNew || isNameNewFormat
    },
    iconName () {
      const name = this.name.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('')
      const changeOldComponentToNew = {
        'IconAchievement': 'IcoAchievement',
        'IconAchievementV52': 'IcoAchievement',
        'IconActivity': 'IcoActivityContent',
        'IconAI': 'IcoAi',
        'IconAlert': 'IcoExclamationTriangle',
        'IconApproval': 'IcoStamp',
        'IconArrowChevronDown': 'IcoChevronDown',
        'IconArrowChevronLeft': 'IcoChevronLeft',
        'IconArrowChevronRight': 'IcoChevronRight',
        'IconArrowChevronUp': 'IcoChevronUp',
        'IconArrowDown': 'IcoArrowDown',
        'IconArrowUpDown': 'IcoArrowUpDown',
        'IconArrowRightCircle': 'IcoArrowRightCircle',
        'IconArticle': 'IcoArticleContent',
        'IconAssessmentReview': 'IcoGauge',
        'IconAudio': 'IcoAudioContent',
        'IconAwardCup': 'IcoTrophy',
        'IconAwardFlag': 'IcoFlagTriangle',
        'IconAwardLeaderBoard': 'IcoWinnerPodium',
        'IconBadge': 'IcoBadge',
        'IconBadgeCoin': 'IcoBadge',
        'IconBox': 'IcoFolderCloud',
        'IconCalendar': 'IcoCalendar',
        'IconCart': 'IcoCart',
        'IconCategory': 'IcoCategory',
        'IconCertificate': 'IcoCertificate',
        'IconChat': 'IcoChat',
        'IconCheckCircleV5': 'IcoCheckCircle',
        'IconClass': 'IcoClassContent',
        'IconClassProgram': 'IcoClassProgramContent',
        'IconCloseCircle': 'IcoXCircle',
        'IconCommoChat': 'IcoCalloutEllipsis',
        'IconCommoDelete': 'IcoTrash',
        'IconCommoHide': 'IcoCalloutX',
        'IconCommoLeave': 'IcoArrowOutRightRectangle',
        'IconCommoMute': 'IcoSpeakerWaveSlash',
        'IconCommoMuted': 'IcoSpeakerWaveSlash',
        'IconCommoPin': 'IcoPin',
        'IconCommoUnhide': 'IcoCallout',
        'IconCommoUnmute': 'IcoSpeakerWave',
        'IconCommoUnpin': 'IcoPinSlash',
        'IconCompany': 'IcoBuilding',
        'IconCompetency': 'IcoCompetency',
        'IconCompetencyCriteria': 'IcoLightBulb',
        'IconCompetencyGap': 'IcoCompetencyGap',
        'IconCompetencyGroup': 'IcoCompetencyGroup',
        'IconCompetencyHeatmap': 'IcoCompetencyVisualization',
        'IconCompetencyLibrary': 'IcoCompetencyLibrary',
        'IconCompetencyRating': 'IcoProficiencyLibrary',
        'IconComplete': 'IcoCheckCircle',
        'IconConiclex': 'IcoConiclexOld',
        'IconContentLock': 'IcoLock',
        'IconContinue': 'IcoPlayTriangleLined',
        'IconCourse': 'IcoCourseContent',
        'IconCreate': 'IcoPlusCircle',
        'IconDashboard': 'IcoDashboard',
        'IconDelete': 'IcoTrash',
        'IconDocument': 'IcoDocumentContent',
        'IconDocumentList': 'IcoPaperEnvelope',
        'IconDownload': 'IcoArrowInDownRectangle',
        'IconEdit': 'IcoPencil',
        'IconEditProfile': 'IcoPersonPencil',
        'IconEditorPick': 'IcoThumbUp',
        'IconEmail': 'IcoEnvelope',
        'IconEmojiTab': 'IcoFaceGrin',
        'IconExplore': 'IcoCompass',
        'IconExternalTraining': 'IcoExternalTrainingContent',
        'IconExternalVideo': 'IcoExternalVideoContent',
        'IconFilter': 'IcoSlider',
        'IconFilterActive': 'IcoSliderCheck',
        'IconFlag': 'IcoFlagTriangle',
        'IconFlagAchievement': 'IcoAchievement',
        'IconFlash': 'IcoThunder',
        'IconThunderV52': 'IcoThunder',
        'IconFlashCard': 'IcoFlashCardContent',
        'IconGridView': 'IcoRow2',
        'IconGrow': 'IcoRocket',
        'IconHome': 'IcoHome',
        'IconIdp': 'IcoDevelopmentPlan',
        'IconInformation': 'IcoICircle',
        'IconInstructor': 'IcoInstructor',
        'IconLangEn': 'IcoLanguageEnglish',
        'IconLangTh': 'IcoLanguageThai',
        'IconLeaderboard': 'IcoWinnerPodium',
        'IconLearn': 'IcoPlayTriangleOrbit',
        'IconLearningProgram': 'IcoLearningProgramContent',
        'IconLearningRequest': 'IcoPaperPencil',
        'IconLeftCircle': 'IcoChevronLeftCircle',
        'IconLibrary': 'IcoMaterialContent',
        'IconLive': 'IcoLiveContent',
        'IconLoading': 'IcoLoadingRing',
        'IconMaterials': 'IcoMaterialContent',
        'IconMenu': 'IcoLine3Horizontal',
        'IcoLine3horizontal': 'IcoLine3Horizontal',
        'IconMessage': 'IcoCalloutEllipsis',
        'IconMore': 'IcoEllipsisVertical',
        'IconMyAssessment': 'IcoPersonGraph',
        'IconNotification': 'IcoBell',
        'IconOnboard': 'IcoPeople3Graph',
        'IcoPeople3graph': 'IcoPeople3Graph',
        'IconPathway': 'IcoPathwayContent',
        'IconPeopleCap': 'IcoPeopleCapability',
        'IconPhone': 'IcoPhone',
        'IconPlay': 'IcoGameController',
        'IconPlayCircle': 'IcoPlayTriangleCircle',
        'IconPlaylist': 'IcoPlaylistContent',
        'IconPointCircle': 'IcoPoints',
        'IconPopular': 'IcoHeart',
        'IconPosition': 'IcoBriefcase',
        'IconProfile': 'IcoPersonCircle',
        'IconPublicLearning': 'IcoPublicLearningContent',
        'IconQrcode': 'IcoQrFrame',
        'IconRatingReview': 'IcoStar',
        'IconReload': 'IcoArrowCounterclockwise',
        'IconRequest': 'IcoCalloutClock',
        'IconReward': 'IcoGift',
        'IconRewardMenuRewardStore': 'IcoRewardStore',
        'IconRightCircle': 'IcoChevronRightCircle',
        'IconScreenshot': 'IcoSquareDashPlus',
        'IconSetting': 'IcoGear',
        'IconShareSquare': 'IcoArrowOutUpRightSquare',
        'IconSorting': 'IcoArrowUpDown',
        'IconSpaces': 'IcoPlayTriangleOrbit',
        'IconSquareClose': 'IcoXSquare',
        'IconSquareEdit': 'IcoPencilSquare',
        'IconSquareLeft': 'IcoChevronLeftSquare',
        'IconSquareRight': 'IcoChevronRightSquare',
        'IconStar': 'IcoStar',
        'IconSurvey': 'IcoSurveyContent',
        'IconSwitchAdmin': 'IcoArrowSwapSquare',
        'IconTaskMonitor': 'IcoPlayTriangleLine3',
        'IconTeamAssessment': 'IcoPeople3Graph',
        'IconTeams': 'IcoPeople3',
        'IconTest': 'IcoTestContent',
        'IconTimeAlert': 'IcoStopWatch',
        'IconUser': 'IcoPersonCircle',
        'IconUserEmail': 'IcoPersonCircle',
        'IconUsers': 'IcoPeople2',
        'IconVideo': 'IcoVideoContent',
        'IconView': 'IcoEye',
        'IconWarning': 'IcoExclamationTriangle',
        'IconXCircleBg': 'IcoXCircle',
        'IconYearOfExperience': 'IcoBriefcaseClock',
        'IconYearOfService': 'IcoBuildingClock',
        'IconCertificateConiverse': 'IcoCertificate',
        'IconCircleClock': 'IcoClock',
        'IconCirclePoint': 'IcoPoints',
        'IconClassLanding': 'IcoClassContent',
        'IconCpd': 'IcoCertificate',
        'IconFacebook': 'IcoFacebookLogo',
        'IconLearningHours': 'IcoClockCheck',
        'IconLine': 'IcoLineLogo',
        'IconMessenger': 'IcoMessengerLogo',
        'IconMicroLearning': 'IcoMaterialContent',
        'IconMyLearningConiverse': 'IcoPlayTriangleOrbit',
        'IconOnlineCourse': 'IcoCourseContent',
        'IconOrganizations': 'IcoPeople3',
        'IconPaperPlane': 'IcoPaperPlane',
        'IconProvider': 'IcoProvider',
        'IconRewardExpired': 'IcoCubeX',
        'IconRewardExplore': 'IcoGift',
        'IconRewardMenuMyPoint': 'IcoPoints',
        'IconRewardMenuMyReward': 'IcoGift',
        'IconRewardMyPoints': 'IcoPoints',
        'IconRewardOutStock': 'IcoCubeX',
        'IconRewardShoppingCart': 'IcoCart',
        'IconRewardSortBy': 'IcoSlider',
        'IconRewardToCancelled': 'IcoNoSign',
        'IconRewardToCompleted': 'IcoCubeCheck',
        'IconRewardTotal': 'IcoGift',
        'IconRewardTotalRedeem': 'IcoCart',
        'IconRewardTotalRedeemPoint': 'IcoStar',
        'IconTransactions': 'IcoCheckCircle',
        'IconUserMultiple': 'IcoPeople2',
        'IconConiverse': 'IcoCircleOrbit',
        'IconInfoHexagon': 'IcoIHexagon',
        'IconQuickMenu': 'IcoDotPlusGrid',
        'IconAddItem': 'IcoSquare2Plus'
      }
      return changeOldComponentToNew[name] || name
    }
  }
}
</script>

<style lang="scss">
.cnc-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  .lego-icon {
    i {
      font-size: v-bind(width + 'px') !important;
    }
  }
}
</style>
